import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, Bus, Clock, User, MapPin, Users, X, Euro, Phone } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const getAuthHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

export default function BusCalendar() {
  const [buses, setBuses] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [expandedDay, setExpandedDay] = useState(null); // Date string of expanded day
  const [viewMode, setViewMode] = useState('buses'); // 'buses' or 'drivers'

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [busesRes, tripsRes, driversRes] = await Promise.all([
        axios.get(`${API}/api/buses`, getAuthHeaders()),
        axios.get(`${API}/api/trips`, getAuthHeaders()),
        axios.get(`${API}/api/users`, getAuthHeaders())
      ]);
      // Filter out personal buses
      const allBuses = busesRes.data || [];
      const publicBuses = allBuses.filter(b => !b.is_personal);
      setBuses(publicBuses);
      setTrips(tripsRes.data);
      // Filter only drivers
      const allUsers = driversRes.data || [];
      const driversList = allUsers.filter(u => u.role === 'driver' || u.is_driver);
      setDrivers(driversList);
    } catch (error) {
      toast.error('Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };

  // Get days for current month view (including padding days)
  const calendarDays = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    const days = [];
    
    // Add padding days from previous month
    const startPadding = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1; // Monday = 0
    for (let i = startPadding; i > 0; i--) {
      const d = new Date(year, month, 1 - i);
      days.push({ date: d, isCurrentMonth: false });
    }
    
    // Add days of current month
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push({ date: new Date(year, month, i), isCurrentMonth: true });
    }
    
    // Add padding days for next month
    const endPadding = 7 - (days.length % 7);
    if (endPadding < 7) {
      for (let i = 1; i <= endPadding; i++) {
        const d = new Date(year, month + 1, i);
        days.push({ date: d, isCurrentMonth: false });
      }
    }
    
    return days;
  }, [currentDate]);

  // Get trips for a specific date and bus
  const getTripsForDateAndBus = (date, busId) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    const dateStr = `${y}-${m}-${d}`;
    return trips.filter(t => t.date === dateStr && t.bus_id === busId);
  };

  // Get all trips for a specific date
  const getTripsForDate = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    const dateStr = `${y}-${m}-${d}`;
    return trips.filter(t => t.date === dateStr);
  };

  // Get free (available) buses for a specific date
  const getFreeBusesForDate = (date) => {
    const dayTrips = getTripsForDate(date);
    const busyBusIds = new Set(dayTrips.filter(t => t.bus_id).map(t => t.bus_id));
    return buses.filter(b => !busyBusIds.has(b.id));
  };

  // Get free (available) drivers for a specific date
  const getFreeDriversForDate = (date) => {
    const dayTrips = getTripsForDate(date);
    const busyDriverIds = new Set(dayTrips.filter(t => t.driver_id).map(t => t.driver_id));
    return drivers.filter(d => !busyDriverIds.has(d.id));
  };

  // Navigate months
  const prevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const monthNames = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 
                      'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];
  const dayNames = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

  // Format date to YYYY-MM-DD in local timezone (not UTC)
  const formatDateLocal = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const today = formatDateLocal(new Date());

  // Color palette for buses
  const busColors = [
    'bg-blue-500', 'bg-green-500', 'bg-purple-500', 'bg-orange-500', 
    'bg-pink-500', 'bg-teal-500', 'bg-indigo-500', 'bg-red-500',
    'bg-yellow-500', 'bg-cyan-500'
  ];

  const getBusColor = (index) => busColors[index % busColors.length];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500"></div>
      </div>
    );
  }

  return (
    <Card className="p-4 sm:p-6">
      <div className="flex flex-col gap-4">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 bg-gradient-to-br ${viewMode === 'buses' ? 'from-green-500 to-emerald-600' : 'from-blue-500 to-indigo-600'} rounded-xl flex items-center justify-center shadow-lg`}>
              {viewMode === 'buses' ? <Bus className="w-5 h-5 text-white" /> : <Users className="w-5 h-5 text-white" />}
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-800">Календарь занятости</h2>
              <p className="text-sm text-gray-500">
                {viewMode === 'buses' ? `${buses.length} автобусов` : `${drivers.length} водителей`}
              </p>
            </div>
          </div>
          
          {/* View Mode Toggle */}
          <div className="flex items-center bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('buses')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                viewMode === 'buses' 
                  ? 'bg-white text-green-700 shadow-sm' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Bus className="w-4 h-4" />
              Автобусы
            </button>
            <button
              onClick={() => setViewMode('drivers')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                viewMode === 'drivers' 
                  ? 'bg-white text-blue-700 shadow-sm' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Users className="w-4 h-4" />
              Водители
            </button>
          </div>
        </div>

        {/* Month Navigation */}
        <div className="flex items-center justify-between bg-gray-50 rounded-xl p-3">
          <Button variant="ghost" size="sm" onClick={prevMonth}>
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-3">
            <span className="text-lg font-bold text-gray-800">
              {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
            </span>
            <Button variant="outline" size="sm" onClick={goToToday}>
              Сегодня
            </Button>
          </div>
          <Button variant="ghost" size="sm" onClick={nextMonth}>
            <ChevronRight className="w-5 h-5" />
          </Button>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap gap-2">
          {viewMode === 'buses' ? (
            <>
              {buses.slice(0, 10).map((bus, index) => (
                <div key={bus.id} className="flex items-center gap-1.5 text-xs">
                  <div className={`w-3 h-3 rounded ${getBusColor(index)}`}></div>
                  <span className="text-gray-600 truncate max-w-[100px]">{bus.license_plate}</span>
                </div>
              ))}
              {buses.length > 10 && (
                <span className="text-xs text-gray-400">+{buses.length - 10} ещё</span>
              )}
            </>
          ) : (
            <>
              {drivers.slice(0, 10).map((driver, index) => (
                <div key={driver.id} className="flex items-center gap-1.5 text-xs">
                  <div className={`w-3 h-3 rounded ${getBusColor(index)}`}></div>
                  <span className="text-gray-600 truncate max-w-[100px]">{driver.full_name || driver.username}</span>
                </div>
              ))}
              {drivers.length > 10 && (
                <span className="text-xs text-gray-400">+{drivers.length - 10} ещё</span>
              )}
            </>
          )}
        </div>

        {/* Main content: Calendar + Sidebar */}
        <div className="flex gap-4 flex-col lg:flex-row">
          {/* Calendar Grid */}
          <div className="flex-1 overflow-x-auto">
            <div className="min-w-[500px]">
              {/* Day Names */}
              <div className="grid grid-cols-7 gap-1 mb-1">
                {dayNames.map(day => (
                  <div key={day} className="text-center text-xs font-semibold text-gray-500 py-2">
                    {day}
                  </div>
                ))}
              </div>

              {/* Days Grid */}
              <div className="grid grid-cols-7 gap-1">
              {calendarDays.map((day, idx) => {
                const dateStr = formatDateLocal(day.date);
                const isToday = dateStr === today;
                const dayTrips = getTripsForDate(day.date);
                const isExpanded = expandedDay === dateStr;
                
                // Get unique buses or drivers for this day based on view mode
                const itemsWithTrips = viewMode === 'buses'
                  ? [...new Set(dayTrips.filter(t => t.bus_id).map(t => t.bus_id))]
                  : [...new Set(dayTrips.filter(t => t.driver_id).map(t => t.driver_id))];
                
                const items = viewMode === 'buses' ? buses : drivers;
                
                return (
                  <div
                    key={idx}
                    onClick={() => dayTrips.length > 0 && setExpandedDay(isExpanded ? null : dateStr)}
                    className={`
                      min-h-[80px] p-1.5 rounded-lg border transition-all
                      ${day.isCurrentMonth ? 'bg-white' : 'bg-gray-50'}
                      ${isToday ? 'ring-2 ring-green-500 border-green-300' : 'border-gray-200'}
                      ${dayTrips.length > 0 ? 'cursor-pointer hover:shadow-md hover:border-blue-300' : ''}
                      ${isExpanded ? 'ring-2 ring-blue-500 border-blue-400 shadow-lg' : ''}
                    `}
                  >
                    {/* Date Number */}
                    <div className={`
                      text-xs font-semibold mb-1 flex items-center justify-between
                      ${day.isCurrentMonth ? 'text-gray-700' : 'text-gray-400'}
                      ${isToday ? 'text-green-600' : ''}
                    `}>
                      <span>{day.date.getDate()}</span>
                      {dayTrips.length > 0 && (
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                          viewMode === 'buses' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                        }`}>
                          {dayTrips.length}
                        </span>
                      )}
                    </div>

                    {/* Bus/Driver indicators (compact view) */}
                    {!isExpanded && (
                      <div className="flex flex-wrap gap-0.5 pointer-events-none">
                        {itemsWithTrips.slice(0, 6).map(itemId => {
                          const itemIndex = items.findIndex(i => i.id === itemId);
                          const itemTrips = viewMode === 'buses' 
                            ? dayTrips.filter(t => t.bus_id === itemId)
                            : dayTrips.filter(t => t.driver_id === itemId);
                          
                          return (
                            <div
                              key={itemId}
                              className={`
                                w-5 h-5 rounded ${getBusColor(itemIndex >= 0 ? itemIndex : 0)} 
                                flex items-center justify-center
                              `}
                            >
                              <span className="text-[8px] font-bold text-white">
                                {itemTrips.length > 1 ? itemTrips.length : ''}
                              </span>
                            </div>
                          );
                        })}
                        {itemsWithTrips.length > 6 && (
                          <div className="w-5 h-5 rounded bg-gray-400 flex items-center justify-center">
                            <span className="text-[8px] font-bold text-white">+{itemsWithTrips.length - 6}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Expanded Day Details */}
            {expandedDay && (
              <div className="mt-3 bg-white rounded-xl border-2 border-blue-200 shadow-lg overflow-hidden">
                {/* Header */}
                <div className={`px-4 py-3 ${viewMode === 'buses' ? 'bg-green-500' : 'bg-blue-500'} text-white flex items-center justify-between`}>
                  <div className="flex items-center gap-2">
                    {viewMode === 'buses' ? <Bus className="w-5 h-5" /> : <Users className="w-5 h-5" />}
                    <span className="font-bold">
                      {new Date(expandedDay).toLocaleDateString('ru-RU', { weekday: 'long', day: 'numeric', month: 'long' })}
                    </span>
                    <span className="bg-white/20 px-2 py-0.5 rounded-full text-sm">
                      {getTripsForDate(new Date(expandedDay)).length} рейсов
                    </span>
                  </div>
                  <button 
                    onClick={(e) => { e.stopPropagation(); setExpandedDay(null); }}
                    className="hover:bg-white/20 rounded-full p-1 transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Content - grouped by bus or driver */}
                <div className="p-4 max-h-[400px] overflow-y-auto">
                  {(() => {
                    const dayTrips = getTripsForDate(new Date(expandedDay));
                    const itemsWithTrips = viewMode === 'buses'
                      ? [...new Set(dayTrips.filter(t => t.bus_id).map(t => t.bus_id))]
                      : [...new Set(dayTrips.filter(t => t.driver_id).map(t => t.driver_id))];
                    const items = viewMode === 'buses' ? buses : drivers;

                    if (itemsWithTrips.length === 0) {
                      return <div className="text-gray-500 text-center py-4">Нет рейсов</div>;
                    }

                    return (
                      <div className="space-y-4">
                        {itemsWithTrips.map(itemId => {
                          const itemIndex = items.findIndex(i => i.id === itemId);
                          const item = items[itemIndex];
                          const itemTrips = viewMode === 'buses' 
                            ? dayTrips.filter(t => t.bus_id === itemId)
                            : dayTrips.filter(t => t.driver_id === itemId);
                          
                          const displayName = viewMode === 'buses' 
                            ? item?.license_plate 
                            : (item?.full_name || item?.username);

                          return (
                            <div key={itemId} className="border rounded-lg overflow-hidden">
                              {/* Item header */}
                              <div className={`px-3 py-2 ${getBusColor(itemIndex >= 0 ? itemIndex : 0)} text-white flex items-center gap-2`}>
                                {viewMode === 'buses' ? <Bus className="w-4 h-4" /> : <User className="w-4 h-4" />}
                                <span className="font-semibold">{displayName || 'Не указан'}</span>
                                <span className="ml-auto bg-white/20 px-2 py-0.5 rounded-full text-xs">
                                  {itemTrips.length} рейс(ов)
                                </span>
                              </div>

                              {/* Trips list */}
                              <div className="divide-y">
                                {itemTrips.sort((a, b) => (a.time_start || '').localeCompare(b.time_start || '')).map((trip, tripIdx) => (
                                  <div key={tripIdx} className="px-3 py-2 hover:bg-gray-50">
                                    <div className="flex items-center gap-3 flex-wrap">
                                      {/* Time */}
                                      <div className="flex items-center gap-1 text-gray-700 font-mono font-bold min-w-[50px]">
                                        <Clock className="w-4 h-4 text-gray-400" />
                                        {trip.time_start || '—'}
                                      </div>

                                      {/* Route */}
                                      {trip.route_name && (
                                        <div className="flex items-center gap-1 text-gray-600">
                                          <MapPin className="w-4 h-4 text-red-400" />
                                          <span className="font-medium">{trip.route_name}</span>
                                        </div>
                                      )}

                                      {/* From -> To */}
                                      {(trip.start_location?.name || trip.end_location?.name) && (
                                        <div className="flex items-center gap-1 text-sm text-gray-500">
                                          <span>{trip.start_location?.name || '?'}</span>
                                          <span>→</span>
                                          <span>{trip.end_location?.name || '?'}</span>
                                        </div>
                                      )}

                                      {/* Driver or Bus (opposite of current view) */}
                                      {viewMode === 'buses' && trip.driver_name && (
                                        <div className="flex items-center gap-1 text-sm text-blue-600">
                                          <User className="w-3.5 h-3.5" />
                                          {trip.driver_name}
                                        </div>
                                      )}
                                      {viewMode === 'drivers' && trip.bus_number && (
                                        <div className="flex items-center gap-1 text-sm text-green-600">
                                          <Bus className="w-3.5 h-3.5" />
                                          {trip.bus_number}
                                        </div>
                                      )}

                                      {/* Payment */}
                                      {trip.payment_amount > 0 && (
                                        <div className="flex items-center gap-1 text-sm text-green-600 font-semibold ml-auto">
                                          <Euro className="w-3.5 h-3.5" />
                                          {trip.payment_amount}€
                                        </div>
                                      )}
                                    </div>

                                    {/* Additional info row */}
                                    {(trip.contact_phone || trip.description) && (
                                      <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                                        {trip.contact_phone && (
                                          <div className="flex items-center gap-1">
                                            <Phone className="w-3 h-3" />
                                            {trip.contact_phone}
                                          </div>
                                        )}
                                        {trip.description && (
                                          <span className="truncate">{trip.description}</span>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}

                  {/* Free buses/drivers section */}
                  {(() => {
                    const expandedDate = new Date(expandedDay);
                    const freeItems = viewMode === 'buses' 
                      ? getFreeBusesForDate(expandedDate)
                      : getFreeDriversForDate(expandedDate);

                    return (
                      <div className="mt-4 pt-4 border-t border-dashed">
                        <div className="flex items-center gap-2 mb-3">
                          <div className={`w-2 h-2 rounded-full ${viewMode === 'buses' ? 'bg-green-400' : 'bg-blue-400'} animate-pulse`}></div>
                          <span className="text-sm font-semibold text-gray-600">
                            Свободны на этот день: {freeItems.length}
                          </span>
                        </div>
                        
                        {freeItems.length === 0 ? (
                          <div className="text-sm text-gray-400 italic">
                            Все {viewMode === 'buses' ? 'автобусы' : 'водители'} заняты
                          </div>
                        ) : (
                          <div className="flex flex-wrap gap-2">
                            {freeItems.map((item, idx) => {
                              const displayName = viewMode === 'buses' 
                                ? item.license_plate 
                                : (item.full_name || item.username);
                              
                              return (
                                <div 
                                  key={item.id}
                                  className={`
                                    px-3 py-1.5 rounded-lg text-sm font-medium
                                    ${viewMode === 'buses' 
                                      ? 'bg-green-50 text-green-700 border border-green-200' 
                                      : 'bg-blue-50 text-blue-700 border border-blue-200'
                                    }
                                    flex items-center gap-1.5
                                  `}
                                >
                                  {viewMode === 'buses' ? (
                                    <Bus className="w-3.5 h-3.5" />
                                  ) : (
                                    <User className="w-3.5 h-3.5" />
                                  )}
                                  {displayName}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}
            </div>
          </div>

          {/* Sidebar: Free buses/drivers list */}
          <div className="lg:w-72 shrink-0">
            <div className={`rounded-xl border-2 ${viewMode === 'buses' ? 'border-green-200 bg-green-50/50' : 'border-blue-200 bg-blue-50/50'} p-4 sticky top-4`}>
              {/* Sidebar Header */}
              <div className="flex items-center gap-2 mb-4">
                <div className={`w-8 h-8 rounded-lg ${viewMode === 'buses' ? 'bg-green-500' : 'bg-blue-500'} flex items-center justify-center`}>
                  {viewMode === 'buses' ? <Bus className="w-4 h-4 text-white" /> : <Users className="w-4 h-4 text-white" />}
                </div>
                <div>
                  <h3 className="font-bold text-gray-800 text-sm">
                    {expandedDay 
                      ? new Date(expandedDay).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })
                      : 'Сегодня'
                    }
                  </h3>
                  <p className={`text-xs ${viewMode === 'buses' ? 'text-green-600' : 'text-blue-600'}`}>
                    Свободны
                  </p>
                </div>
              </div>

              {/* Free items list */}
              {(() => {
                const targetDate = expandedDay ? new Date(expandedDay) : new Date();
                const freeItems = viewMode === 'buses' 
                  ? getFreeBusesForDate(targetDate)
                  : getFreeDriversForDate(targetDate);
                const busyItems = viewMode === 'buses'
                  ? buses.filter(b => !freeItems.some(f => f.id === b.id))
                  : drivers.filter(d => !freeItems.some(f => f.id === d.id));

                return (
                  <div className="space-y-3">
                    {/* Free section */}
                    <div>
                      <div className="flex items-center gap-1.5 mb-2">
                        <div className={`w-2 h-2 rounded-full ${viewMode === 'buses' ? 'bg-green-400' : 'bg-blue-400'} animate-pulse`}></div>
                        <span className="text-xs font-semibold text-gray-500 uppercase">
                          Свободны ({freeItems.length})
                        </span>
                      </div>
                      {freeItems.length === 0 ? (
                        <div className="text-xs text-gray-400 italic pl-3">
                          Все заняты
                        </div>
                      ) : (
                        <div className="space-y-1.5">
                          {freeItems.map((item) => {
                            const displayName = viewMode === 'buses' 
                              ? item.license_plate 
                              : (item.full_name || item.username);
                            
                            return (
                              <div 
                                key={item.id}
                                className={`
                                  px-3 py-2 rounded-lg text-sm font-medium
                                  ${viewMode === 'buses' 
                                    ? 'bg-white border border-green-200 text-green-700' 
                                    : 'bg-white border border-blue-200 text-blue-700'
                                  }
                                  flex items-center gap-2 shadow-sm
                                `}
                              >
                                {viewMode === 'buses' ? (
                                  <Bus className="w-4 h-4 opacity-60" />
                                ) : (
                                  <User className="w-4 h-4 opacity-60" />
                                )}
                                <span className="truncate">{displayName}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* Busy section */}
                    {busyItems.length > 0 && (
                      <div className="pt-3 border-t border-dashed">
                        <div className="flex items-center gap-1.5 mb-2">
                          <div className="w-2 h-2 rounded-full bg-red-400"></div>
                          <span className="text-xs font-semibold text-gray-500 uppercase">
                            Заняты ({busyItems.length})
                          </span>
                        </div>
                        <div className="space-y-1.5">
                          {busyItems.map((item) => {
                            const displayName = viewMode === 'buses' 
                              ? item.license_plate 
                              : (item.full_name || item.username);
                            
                            return (
                              <div 
                                key={item.id}
                                className="px-3 py-2 rounded-lg text-sm font-medium bg-gray-100 text-gray-500 flex items-center gap-2"
                              >
                                {viewMode === 'buses' ? (
                                  <Bus className="w-4 h-4 opacity-40" />
                                ) : (
                                  <User className="w-4 h-4 opacity-40" />
                                )}
                                <span className="truncate">{displayName}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 pt-4 border-t">
          <div className={`${viewMode === 'buses' ? 'bg-green-50' : 'bg-blue-50'} rounded-xl p-3 text-center`}>
            <div className={`text-2xl font-bold ${viewMode === 'buses' ? 'text-green-600' : 'text-blue-600'}`}>
              {viewMode === 'buses' ? buses.length : drivers.length}
            </div>
            <div className={`text-xs ${viewMode === 'buses' ? 'text-green-600/70' : 'text-blue-600/70'}`}>
              {viewMode === 'buses' ? 'Автобусов' : 'Водителей'}
            </div>
          </div>
          <div className="bg-purple-50 rounded-xl p-3 text-center">
            <div className="text-2xl font-bold text-purple-600">
              {trips.filter(t => {
                const tripDate = new Date(t.date);
                return tripDate.getMonth() === currentDate.getMonth() && 
                       tripDate.getFullYear() === currentDate.getFullYear();
              }).length}
            </div>
            <div className="text-xs text-purple-600/70">Рейсов в месяце</div>
          </div>
          <div className="bg-orange-50 rounded-xl p-3 text-center">
            <div className="text-2xl font-bold text-orange-600">
              {trips.filter(t => t.date === today).length}
            </div>
            <div className="text-xs text-orange-600/70">Рейсов сегодня</div>
          </div>
          <div className="bg-gray-50 rounded-xl p-3 text-center">
            <div className="text-2xl font-bold text-gray-600">
              {viewMode === 'buses' 
                ? buses.filter(bus => !trips.some(t => t.date === today && t.bus_id === bus.id)).length
                : drivers.filter(driver => !trips.some(t => t.date === today && t.driver_id === driver.id)).length
              }
            </div>
            <div className="text-xs text-gray-600/70">
              {viewMode === 'buses' ? 'Свободно сегодня' : 'Свободны сегодня'}
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}
